import { Type } from '@angular/core';
import { GsFormSchema } from '../../components';
import { ConfigModel, IconWeight, GsStructComponent } from '../../models';

export interface BasicComponentDefinition<GroupType> {
  /** The name of the component to display in the sidemenu (has to be unique) */
  name: string;
  /** The group in wich the component should get displayed (shouldnt be too many groups) */
  group: GroupType;
  /** The icon name to display above the component to recongnize it faster (should be unique) */
  iconName: string;
  /** The icon type to display */
  iconWeight?: IconWeight;
  /**
   * Lazy loader for the corresponding Angular component class. MUST target the concrete module file
   * (NOT the '../../template' barrel), otherwise esbuild cannot code-split it into its own chunk.
   * Resolved on demand by LazyComponentHostDirective so a page only loads the components it renders.
   */
  loadComponent: () => Promise<Type<unknown>>;
  /**
   * The formSchema for the settings of this component. Designer-only metadata — provided eagerly in the
   * Designer (via `PAGE_COMPONENTS_WITH_SCHEMAS_PROVIDER`) but omitted from the public CloudFront registry
   * so the ~56 schema definitions tree-shake out of its bundle. Therefore optional.
   */
  formSchema?: GsFormSchema;
  /** The modules from bc, the customer needs to have ONE of them to use the component */
  bcModules: BcModule[];
  /** Only visible in the palette for accounts with `RIGHT_PAGE_DESIGN_ADVANCED` (GS staff) */
  adminOnly?: boolean;
}

/** All possible bc-modules bookable by the customer */
export type BcModule =
  | '*'
  | 'website'
  | 'webshop'
  | 'voucher'
  | 'social'
  | 'local-seo'
  | 'accounting'
  | 'rechtssicherheit'
  | 'uberall'
  | 'b2c'
  | 'events'
  | 'loyalty'
  | 'loyalty-web'
  | 'dam'
  | 'plant-doctor';

export interface ComponentWithType<ComponentType> extends BasicComponentDefinition<unknown> {
  type: ComponentType;
}

export abstract class ComponentsHelper<
  ComponentType extends string | number | symbol,
  ComponentDefinition extends BasicComponentDefinition<GroupType>,
  GroupType extends string
> {
  /** The components to display in the sidemenu */
  abstract components: Record<ComponentType, ComponentDefinition>;
  /** The translation prefix for the groups of the components, to display the groupnames in the right language */
  public groupTranslationPrefix: string = 'components.groups.';
  /** The priority of the groups to display them in the right order in the sidemenu (lower number = higher priority) */
  abstract groupPriorities: Record<GroupType, number>;

  /**
   * Get the component definition by type
   * @param type of the component
   * @returns the component definition
   */
  getComponentDefinition(type?: ComponentType): ComponentDefinition | undefined {
    if (!type) return undefined;
    return this.components[type];
  }

  /**
   * Get all components of a specific group
   * @param group to filter the components by
   * @returns all components of the specified group
   */
  getComponentsByGroup(group: GroupType): ComponentWithType<ComponentType>[] {
    return Object.keys(this.components)
      .map((key) => {
        return { ...this.components[key as ComponentType], type: key as ComponentType };
      })
      .filter((c) => c.group === group);
  }

  /**
   * Get all groups
   * @returns all groups of the components correctly sorted
   */
  getComponentGroups(): GroupType[] {
    const componentsArray: Array<ComponentDefinition> = Object.values(this.components);
    const groups = componentsArray
      .map((c) => c.group)
      .sort((a, b) => {
        const priorityA = this.groupPriorities[a] ?? Number.MAX_SAFE_INTEGER;
        const priorityB = this.groupPriorities[b] ?? Number.MAX_SAFE_INTEGER;
        return priorityA - priorityB;
      });
    return Array.from(new Set(groups));
  }

  /**
   * Get all component types
   * @returns all component types
   */
  getComponentTypes(): string[] {
    return Object.keys(this.components);
  }

  /**
   * Get the default settings for a component
   * @param type of the component
   * @param config the config to get the default settings from
   * @returns the default settings for the component
   */
  abstract getComponentDefaultSettings(type: ComponentType, config?: ConfigModel): GsStructComponent<unknown> | undefined;
}
